import type { MarkOutcome, Span } from '@wiztime/core';
import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { KeyboardKey } from '../../../lib/input';
import { sameSpan } from '../../../lib/span';
import { useDayUi } from '../dayUiStore';
import { edgeScrollVelocity, scrollStepPx } from './autoScroll';
import type { DragPoint } from './useLaneInteractions';
import { laneDropTargetOf } from './laneDropTargets';
import { beginQuickMarkWrite, endQuickMarkWrite, isQuickMarkWriteBusy } from './quickMarkGuard';
import { horizontalScrollContainerOf, scrollContainerOf } from './scrollContainers';
import {
  elementsAtPoint,
  LANE_SELECTOR,
  laneAtPoint,
  movedBeyondThreshold,
  tileDragAllowed,
  tileDropSpan,
} from './tileDrag';

/**
 * Dragging a quick-mark tile onto the timeline (2026-10c §2), mouse and pen only. The press is
 * captured on the tile; after 5px the drag starts. Every move looks up the lane under the
 * pointer (the week view has seven), snaps the block start to the lane's precision and shows the
 * drop ghost (drawn by the lane from `dayUiStore.tileDrag`) and the floating time tip. Releasing
 * over a lane creates one block of the project's quick duration through the same write path and
 * in-flight guard as a quick-mark click; Esc, a cancelled or lost pointer, and a drop outside the
 * lanes write nothing. The quick-mark mode (Off, Once, Locked) is never read or changed here.
 */

/**
 * Where the pointer is during a drag: over a lane that can take the block, over a lane that
 * cannot (loading, overlay open, another write in flight: the ghost shows not-allowed), or
 * over no lane.
 */
export const DropTarget = { Lane: 'lane', Blocked: 'blocked', None: 'none' } as const;
export type DropTarget = (typeof DropTarget)[keyof typeof DropTarget];

interface TileDragInput {
  readonly projectId: string;
  /** Effective quick duration of the project (`effectiveQuickDurationMin`). */
  readonly durationMin: number;
  readonly createBlock: (date: string, span: Span, projectId: string) => Promise<MarkOutcome>;
}

interface Session {
  readonly pointerId: number;
  readonly startX: number;
  readonly startY: number;
  readonly target: HTMLElement;
  dragging: boolean;
  lastX: number;
  lastY: number;
  lane: HTMLElement | null;
  drop: { readonly date: string; readonly span: Span } | null;
  scrollRaf: number | null;
  /**
   * Sideways auto-scroll is armed once the pointer has been inside the week grid, clear of its
   * edge bands: a tile picked from the sidebar beside the grid must not scroll the days on its
   * way in (the pointer starts beyond the grid's edge).
   */
  horizontalArmed: boolean;
}

export interface TileDragHandlers {
  readonly onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly onLostPointerCapture: (event: ReactPointerEvent<HTMLElement>) => void;
}

export interface TileDrag {
  readonly handlers: TileDragHandlers;
  /** A drag is in progress (cursor feedback). */
  readonly dragging: boolean;
  readonly dropTarget: DropTarget;
  /** Span the tip shows; null when no lane can take the block. */
  readonly tipSpan: Span | null;
  readonly pointRef: { readonly current: DragPoint | null };
  /** True once for the click that trails a completed drag: the tile must not enter brush mode. */
  consumeClick(): boolean;
}

export function useTileDrag(input: TileDragInput): TileDrag {
  const [dragging, setDragging] = useState(false);
  const [tipSpan, setTipSpan] = useState<Span | null>(null);
  const [blocked, setBlocked] = useState(false);
  const session = useRef<Session | null>(null);
  /** True while the click that trails a drag or an Esc cancel must not reach the tile. */
  const swallowClick = useRef(false);
  /** Removes the window listeners that lift `swallowClick` when no click is going to arrive. */
  const stopSwallow = useRef<(() => void) | null>(null);
  const pointRef = useRef<DragPoint | null>(null);
  const latest = useRef(input);
  useEffect(() => {
    latest.current = input;
  });
  /** Removes the Esc listener of the drag in progress. */
  const stopEscape = useRef<(() => void) | null>(null);

  /** Look up the lane under the pointer and refresh the ghost, the tip and the drop result. */
  const updateDrop = useCallback((s: Session): void => {
    const lane = laneAtPoint(elementsAtPoint(s.lastX, s.lastY));
    const target = lane === null ? null : laneDropTargetOf(lane);
    let preview: { readonly date: string; readonly span: Span; readonly allowed: boolean } | null = null;
    if (lane !== null && target !== null) {
      const rect = lane.getBoundingClientRect();
      const span = tileDropSpan(
        { top: rect.top, height: rect.height, dayStartMin: target.options.dayStartMin },
        s.lastY,
        target.options,
        target.obstacles,
        latest.current.durationMin,
      );
      if (span !== null)
        preview = { date: target.date, span, allowed: target.ready && !isQuickMarkWriteBusy() };
    }
    if (lane !== null) s.lane = lane;
    s.drop = preview !== null && preview.allowed ? { date: preview.date, span: preview.span } : null;
    useDayUi.getState().setTileDrag(preview);
    setBlocked(preview !== null && !preview.allowed);
    const shown = s.drop?.span ?? null;
    setTipSpan((previous) => (sameSpan(previous, shown) ? previous : shown));
  }, []);

  const end = useCallback((s: Session, release: boolean): void => {
    if (s.scrollRaf !== null) window.cancelAnimationFrame(s.scrollRaf);
    s.scrollRaf = null;
    stopEscape.current?.();
    stopEscape.current = null;
    if (session.current === s) session.current = null;
    if (release) {
      try {
        if (s.target.hasPointerCapture(s.pointerId)) s.target.releasePointerCapture(s.pointerId);
      } catch {
        // The capture may already be gone (the element left the document).
      }
    }
    pointRef.current = null;
    useDayUi.getState().setTileDrag(null);
    setDragging(false);
    setTipSpan(null);
    setBlocked(false);
  }, []);

  const disarmSwallow = useCallback((): void => {
    swallowClick.current = false;
    stopSwallow.current?.();
    stopSwallow.current = null;
  }, []);

  /**
   * The next click on the tile must not arm the brush. The flag holds until that click, the next
   * pointer press or key press anywhere, or - when the release has not happened yet (Esc during
   * a held press) - a release away from the tile, after which no click arrives.
   */
  const armSwallow = useCallback(
    (tile: HTMLElement, released: boolean): void => {
      disarmSwallow();
      swallowClick.current = true;
      const lift = (): void => disarmSwallow();
      const onPointerUp = (event: PointerEvent): void => {
        if (!(event.target instanceof Node) || !tile.contains(event.target)) disarmSwallow();
      };
      const onKeyDown = (event: KeyboardEvent): void => {
        if (!event.repeat) disarmSwallow();
      };
      window.addEventListener('pointerdown', lift, true);
      window.addEventListener('pointercancel', lift, true);
      window.addEventListener('keydown', onKeyDown, true);
      if (!released) window.addEventListener('pointerup', onPointerUp, true);
      stopSwallow.current = () => {
        window.removeEventListener('pointerdown', lift, true);
        window.removeEventListener('pointercancel', lift, true);
        window.removeEventListener('keydown', onKeyDown, true);
        window.removeEventListener('pointerup', onPointerUp, true);
      };
    },
    [disarmSwallow],
  );

  /** Keep the nearest scroll containers scrolling while the pointer rides their edge. */
  const ensureAutoScroll = useCallback(
    (s: Session): void => {
      if (s.scrollRaf !== null) return;
      let lastTime = performance.now();
      const step = (time: number): void => {
        s.scrollRaf = null;
        if (session.current !== s || !s.dragging) return;
        const elapsedMs = time - lastTime;
        lastTime = time;
        const lane = s.lane ?? document.querySelector<HTMLElement>(LANE_SELECTOR);
        const vertical = scrollContainerOf(lane);
        const horizontalCandidate = horizontalScrollContainerOf(lane);
        // Only the week grid scrolls sideways during a drag: a container holding several lanes.
        const horizontal =
          horizontalCandidate !== null && horizontalCandidate.querySelectorAll(LANE_SELECTOR).length > 1
            ? horizontalCandidate
            : null;
        const viewportRect = (
          container: HTMLElement,
        ): DOMRect | { top: number; bottom: number; left: number; right: number } =>
          container === document.scrollingElement
            ? { top: 0, bottom: window.innerHeight, left: 0, right: window.innerWidth }
            : container.getBoundingClientRect();
        let scrolled = false;
        if (vertical !== null) {
          const rect = viewportRect(vertical);
          // The pointer must be above or below the scroller itself, not beside it (card column).
          if (s.lastX >= rect.left && s.lastX <= rect.right) {
            const delta = scrollStepPx(edgeScrollVelocity(s.lastY, rect.top, rect.bottom), elapsedMs);
            if (delta !== 0) {
              const before = vertical.scrollTop;
              vertical.scrollTop = before + delta;
              scrolled = vertical.scrollTop !== before;
            }
          }
        }
        if (horizontal !== null) {
          const rect = viewportRect(horizontal);
          const velocity = edgeScrollVelocity(s.lastX, rect.left, rect.right);
          if (velocity === 0 && s.lastX > rect.left && s.lastX < rect.right) s.horizontalArmed = true;
          if (s.horizontalArmed && s.lastY >= rect.top && s.lastY <= rect.bottom) {
            const delta = scrollStepPx(velocity, elapsedMs);
            if (delta !== 0) {
              const before = horizontal.scrollLeft;
              horizontal.scrollLeft = before + delta;
              scrolled = scrolled || horizontal.scrollLeft !== before;
            }
          }
        }
        // The pointer stands still while the content moves: snap again from the same position.
        if (scrolled) updateDrop(s);
        s.scrollRaf = window.requestAnimationFrame(step);
      };
      s.scrollRaf = window.requestAnimationFrame(step);
    },
    [updateDrop],
  );

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>): void => {
      disarmSwallow();
      if (!event.isPrimary || !tileDragAllowed(event.pointerType, event.button)) return;
      if (session.current !== null) end(session.current, true);
      session.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        target: event.currentTarget,
        dragging: false,
        lastX: event.clientX,
        lastY: event.clientY,
        lane: null,
        drop: null,
        scrollRaf: null,
        horizontalArmed: false,
      };
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // No capture (synthetic events, detached element): window-level moves still reach the tile.
      }
    },
    [end, disarmSwallow],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLElement>): void => {
      const s = session.current;
      if (s === null || event.pointerId !== s.pointerId) return;
      s.lastX = event.clientX;
      s.lastY = event.clientY;
      if (!s.dragging) {
        if (!movedBeyondThreshold(s.startX, s.startY, event.clientX, event.clientY)) return;
        s.dragging = true;
        setDragging(true);
        // Esc cancels the drag before the page's Esc chain (which would leave the brush mode).
        const onKeyDown = (keyEvent: KeyboardEvent): void => {
          if (keyEvent.key !== KeyboardKey.Escape) return;
          keyEvent.stopImmediatePropagation();
          keyEvent.preventDefault();
          end(s, true);
          // The button may still be held: the release over the tile would click it.
          armSwallow(s.target, false);
        };
        window.addEventListener('keydown', onKeyDown, true);
        stopEscape.current = () => window.removeEventListener('keydown', onKeyDown, true);
      }
      pointRef.current = { x: event.clientX, y: event.clientY, touch: false };
      updateDrop(s);
      ensureAutoScroll(s);
    },
    [end, updateDrop, ensureAutoScroll, armSwallow],
  );

  const finish = useCallback(
    (event: ReactPointerEvent<HTMLElement>, commit: boolean): void => {
      const s = session.current;
      if (s === null || event.pointerId !== s.pointerId) return;
      const wasDragging = s.dragging;
      if (wasDragging && commit) {
        s.lastX = event.clientX;
        s.lastY = event.clientY;
        updateDrop(s);
      }
      const drop = commit ? s.drop : null;
      end(s, true);
      if (!wasDragging) return;
      // The click that trails the release must not reach the tile (it would arm the brush).
      armSwallow(s.target, true);
      if (drop === null || !beginQuickMarkWrite()) return;
      // Same write path as a quick-mark click: the command's own toasts, focus and duplicate
      // handling apply; the outcome never touches the quick-mark mode.
      void latest.current
        .createBlock(drop.date, drop.span, latest.current.projectId)
        .finally(endQuickMarkWrite);
    },
    [end, updateDrop, armSwallow],
  );

  const onPointerUp = useCallback((event: ReactPointerEvent<HTMLElement>) => finish(event, true), [finish]);
  const onPointerCancel = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => finish(event, false),
    [finish],
  );
  const onLostPointerCapture = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const s = session.current;
      if (s === null || event.pointerId !== s.pointerId) return;
      finish(event, false);
    },
    [finish],
  );

  useEffect(
    () => () => {
      const s = session.current;
      if (s !== null) end(s, false);
      disarmSwallow();
    },
    [end, disarmSwallow],
  );

  const consumeClick = useCallback((): boolean => {
    const swallowed = swallowClick.current;
    disarmSwallow();
    return swallowed;
  }, [disarmSwallow]);

  return {
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onLostPointerCapture },
    dragging,
    dropTarget: blocked ? DropTarget.Blocked : tipSpan === null ? DropTarget.None : DropTarget.Lane,
    tipSpan,
    pointRef,
    consumeClick,
  };
}
